import React, { Component } from 'react';
import { connect } from 'react-redux';
import { isEqual } from 'lodash-es';
import { withRouter } from 'react-router-dom';
import { RootState, StoreProps } from 'store';
import { addPermissions, WithPermissions } from 'decorators/addPermissions';
import { WithRouterProps } from 'decorators/withRouter';
import {
  getData,
  saveDataState,
  changeDataForceUpdate,
  changeDataOffset,
} from 'actions/getData';
import { AnyObject } from 'types/Common';
import SearchFilters from 'types/SearchFilters';
import getFiltersValues from 'selectors/getFiltersValues';
import tableNames from 'constants/tableNames';
import Messages from 'constants/rpcTypes';
import CONFIG from 'config';
import path from 'helpers/path';
import {
  prepareDataForState,
  foundQuickFilters,
  getPathUrl,
} from 'helpers/paymentsPayoutsRequest';
import DynamicTable from 'components/ui/table/DynamicTable';

interface OwnProps {
  tableName: tableNames;
  isLoading?: boolean;
  apiRequestLimit?: number;
  resizableColumns?: boolean;
  customClass?: string;
  minColumnsWidth?: { [key: string]: number };
  forbidResizeFor?: string[];
  headerRender?: React.ReactNode;
  rowRender?: (data: any, index: number) => React.ReactNode;
  apiRequest: (data?: any) => Promise<any>;
  getTableRef?: (ref: HTMLElement) => void;
  rowHeight?: number;
  headerHeight?: number;
  horizontalScrollHeight?: number;
}

interface ConnectedProps {
  user: AnyObject;
  itemsList: {
    isFetch: boolean;
    isFixed: false;
    items: any[];
    sort: {
      field: string;
      order: 'desc' | 'asc';
    };
    totalItems: number;
    offset: number;
    forceUpdate?: boolean;
    isFetched?: boolean;
  };
  filters: AnyObject;
  quickFilters: AnyObject;
  searchFilters: {
    list: SearchFilters;
    selectedCount: number;
  };
}

type Props = OwnProps &
  ConnectedProps &
  WithPermissions &
  StoreProps &
  WithRouterProps;

interface State {
  offset: number;
}

class DataListContainer extends Component<Props, State> {
  fetchTimerId: NodeJS.Timeout | undefined = undefined;
  wasInitialFetch = false;

  componentDidMount() {
    const { itemsList } = this.props;

    if (itemsList.items.length === 0) {
      this.fetchItems();
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    const {
      tableName: currentTableName,
      user: { timezone: currentTimeZone },
      itemsList: {
        isFetched,
        sort: currentSort,
        forceUpdate: currentForceUpdate,
      },
      filters: currentFilters,
      quickFilters: currentQuickFilters,
      searchFilters: currentSearchFilters,
      dispatch,
    } = this.props;
    const {
      tableName: prevTableName,
      user: { timezone: prevTimeZone },
      itemsList: { sort: prevSort, forceUpdate: prevForceUpdate },
      filters: prevFilters,
      quickFilters: prevQuickFilters,
      searchFilters: prevSearchFilters,
    } = prevProps;

    // fetch data if quick-filters changed only in current table
    if (
      prevTableName === currentTableName &&
      !isEqual(prevQuickFilters, currentQuickFilters)
    ) {
      return this.fetchItems();
    }

    // fetch data if one page has multiple tables
    if (prevTableName !== currentTableName && !isFetched) {
      this.wasInitialFetch = false;
      return this.fetchItems();
    }

    // fetch data if filters changed only in current table
    if (
      prevTableName === currentTableName &&
      !isEqual(prevFilters, currentFilters)
    ) {
      return this.fetchItems();
    }

    if (!isEqual(prevSearchFilters, currentSearchFilters)) {
      return this.fetchItems();
    }

    // fetch data if sorting changed only in current table
    if (prevTableName === currentTableName && !isEqual(prevSort, currentSort)) {
      return this.fetchItems();
    }

    if (prevTimeZone !== currentTimeZone) {
      return this.fetchItems();
    }

    if (!prevForceUpdate && currentForceUpdate) {
      this.fetchItems();
      return dispatch(changeDataForceUpdate(currentTableName, false));
    }
  }

  componentWillUnmount() {
    const { dispatch, tableName } = this.props;
    dispatch(saveDataState(tableName, this.state));
  }

  canViewItem = () => {
    const { tableName, isEnabled } = this.props;
    if (['payments', 'payouts'].includes(tableName)) {
      return isEnabled(Messages.Payments_Payment);
    }
    return true;
  };

  goToItem = (data: any) => {
    const { tableName, history } = this.props;

    if (this.canViewItem()) {
      const pathUrl = getPathUrl(tableName, data);

      if (pathUrl) {
        history.push(path(pathUrl));
      }
    }
  };

  fetchItems = (newOffset?: number) => {
    const delay = 1000;
    const { filters, searchFilters, itemsList } = this.props;
    clearTimeout(this.fetchTimerId);

    return new Promise<void>((resolve) => {
      if (
        !itemsList.forceUpdate &&
        this.wasInitialFetch &&
        newOffset === undefined &&
        foundQuickFilters({ filters, searchFilters })
      ) {
        this.fetchTimerId = setTimeout(() => {
          this.fetchItemsStart(resolve, newOffset);
        }, delay);
      } else {
        this.fetchItemsStart(resolve, newOffset);
      }
    });
  };

  fetchItemsStart = (onResolve: () => void, newOffset?: number) => {
    const {
      quickFilters,
      filters,
      searchFilters,
      itemsList: { sort },
      tableName,
      dispatch,
      apiRequest,
      apiRequestLimit = CONFIG.REQUEST_ITEMS_LIMIT,
    } = this.props;

    const baseRequest = {
      pagination: {
        limit: apiRequestLimit,
        offset: newOffset ?? 0,
      },
      sort,
      filter: {},
      saveFilter: false,
    };

    const request = prepareDataForState(
      { quickFilters, filters, searchFilters },
      baseRequest,
      { tableName }
    );

    dispatch(changeDataOffset(tableName, newOffset ?? 0));

    dispatch(
      getData({
        apiRequest,
        request,
        onResolve,
        isUpload: Boolean(newOffset),
        name: tableName,
      })
    );
    this.wasInitialFetch = true;
  };

  fetchNextPageItems = () => {
    const {
      itemsList: { offset },
      apiRequestLimit = CONFIG.REQUEST_ITEMS_LIMIT,
    } = this.props;

    return this.fetchItems(offset + apiRequestLimit);
  };

  render() {
    const {
      isLoading,
      itemsList,
      tableName,
      headerRender,
      customClass,
      forbidResizeFor,
      rowRender,
      getTableRef,
      minColumnsWidth = {},
      resizableColumns = true,
      rowHeight,
      headerHeight,
      horizontalScrollHeight,
    } = this.props;
    const { isFetch, isFetched, items, totalItems } = itemsList;
    const thereAreItems = isFetched ? !!items.length : resizableColumns!;

    return (
      <DynamicTable
        isFetch={isFetch || isLoading}
        resizableColumns={resizableColumns! && thereAreItems}
        forbidResizeFor={forbidResizeFor}
        items={items}
        rowRender={rowRender}
        headerRender={headerRender}
        totalItems={totalItems}
        tableName={tableName}
        minColumnsWidth={minColumnsWidth}
        getTableRef={getTableRef}
        loadNextPage={this.fetchNextPageItems}
        clickToItem={this.goToItem}
        canViewItem={this.canViewItem()}
        customClass={customClass}
        rowHeight={rowHeight}
        headerHeight={headerHeight}
        horizontalScrollHeight={horizontalScrollHeight}
      />
    );
  }
}

const mapStateToProps = (
  state: RootState,
  props: OwnProps
): ConnectedProps => ({
  user: state.user,
  itemsList: state.data[props.tableName],
  quickFilters: state.quickFilters[props.tableName],
  searchFilters: state.searchFilters,
  filters: getFiltersValues(state, props.tableName),
});

export default connect(mapStateToProps)(
  withRouter(addPermissions(DataListContainer))
);
